import { memo, useCallback, useMemo } from 'react';
import {
  ConstellationSpec as ConstellationSpecType,
  SingularStationArea,
  StationaryArea,
} from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { SelectInput } from '@/modules/common/components/inputs';
import { Spacing } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/Spacing';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { NumUtils } from '@/modules/common/utils';
import { useCanvasStore } from '@/modules/canvas/store';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { poiMapper, stationMapper } from '@/modules/canvas/mappers';
import { getDuplicateStationIds } from '@/modules/canvas/mappers/stationMapper';
import { rotatePoint } from '@/modules/common/helpers/math';
import { Angle } from './Angle';
import { ANGLE_EPSILON_LOOSE as ANGLE_EPSILON } from '@/helpers/angleConstants';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { getCommonOption, getCommonNumber } from '@/modules/common/helpers/commonValue';
import { getLoadHandlingAngle, getSpacingIconRotationDeg } from './constellationSpecHelpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const DISTRIBUTION_OPTIONS: {
  labelKey: string;
  value: ConstellationSpecType['distribution'];
}[] = [
  { labelKey: 'interface:properties.area.distribution_options.margin', value: 'MARGIN' },
  { labelKey: 'interface:properties.area.distribution_options.gap', value: 'GAP' },
];

const STACKING_OPTIONS: {
  labelKey: string;
  value: ConstellationSpecType['stacking'];
}[] = [
  {
    labelKey: 'interface:properties.area.stacking_options.horizontal_top',
    value: 'HORIZONTAL_TOP',
  },
  {
    labelKey: 'interface:properties.area.stacking_options.horizontal_middle',
    value: 'HORIZONTAL_MIDDLE',
  },
  {
    labelKey: 'interface:properties.area.stacking_options.horizontal_bottom',
    value: 'HORIZONTAL_BOTTOM',
  },
  { labelKey: 'interface:properties.area.stacking_options.vertical_left', value: 'VERTICAL_LEFT' },
  {
    labelKey: 'interface:properties.area.stacking_options.vertical_middle',
    value: 'VERTICAL_MIDDLE',
  },
  {
    labelKey: 'interface:properties.area.stacking_options.vertical_right',
    value: 'VERTICAL_RIGHT',
  },
];

interface Props {
  selectedAreas: SingularStationArea[] | StationaryArea[];
  preferenceKey: 'singularStationArea.constellationSpec' | 'stationaryArea.constellationSpec'; // determines which type of constellation spec is being used based on the area type
}

function ConstellationSpecComponent({ selectedAreas, preferenceKey }: Props) {
  const distributionOptions = useTranslatedOptions(DISTRIBUTION_OPTIONS);
  const stackingOptions = useTranslatedOptions(STACKING_OPTIONS);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const updateConstellationSpecs = useCallback(
    (update: (spec: ConstellationSpecType) => ConstellationSpecType) => {
      const updatedAreas = createEmptyAreas();

      selectedAreas.forEach((area) =>
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          constellationSpec: update(area.constellationSpec),
        }),
      );

      fddmSvc.updateAreas(updatedAreas);
    },
    [selectedAreas, fddmSvc],
  );

  const stacking = useMemo(() => {
    const values = selectedAreas.map(({ constellationSpec }) => constellationSpec.stacking);
    return getCommonOption(values);
  }, [selectedAreas]);

  const distribution = useMemo(() => {
    const values = selectedAreas.map(({ constellationSpec }) => constellationSpec.distribution);
    return getCommonOption(values);
  }, [selectedAreas]);

  const minMargin = useMemo(
    () =>
      getCommonNumber(
        selectedAreas.map(({ constellationSpec }) => constellationSpec.spacing.minMargin),
      ),
    [selectedAreas],
  );

  const minGap = useMemo(
    () =>
      getCommonNumber(
        selectedAreas.map(({ constellationSpec }) => constellationSpec.spacing.minGap),
      ),
    [selectedAreas],
  );

  const angle = useMemo(
    () => getCommonNumber(selectedAreas.map(({ constellationSpec }) => constellationSpec.angle)),
    [selectedAreas],
  );

  const parentRotationRad = useMemo(
    () =>
      getCommonNumber(
        selectedAreas.map((area) => area.rectangle.angle + getLoadHandlingAngle(area)),
      ),
    [selectedAreas],
  );

  const spacingIconRotationDeg = useMemo(
    () => getCommonNumber(selectedAreas.map(getSpacingIconRotationDeg)) ?? 0,
    [selectedAreas],
  );

  const isSingleArea = selectedAreas.length === 1;

  /**
   * Rotates POI and station canvas elements around the area center while dragging.
   * Preview only; backend regenerates final positions on commit.
   */
  const onAnglePreview = useCallback(
    (newAngleRad: number) => {
      const [area] = selectedAreas;
      if (!area || !area.constellationSpec) return;

      const canvas = useCanvasStore.getState().instance;
      if (!canvas) return;

      const { floorplan } = useFloorplanDesignStore.getState();
      if (!floorplan) return;

      const deltaRad = newAngleRad - area.constellationSpec.angle;
      if (Math.abs(deltaRad) < ANGLE_EPSILON) return;

      const areaStations = floorplan.stations.filter((station) =>
        station.areaIds.includes(area.id),
      );
      const stationsById = new Map(areaStations.map((station) => [station.id, station]));

      // Constellation direction changes facing angle and rotates the configured vehicle offset.
      // Station centers stay fixed; POI pose and POI rectangle centers rotate around their linked station.
      const rotatedPois = floorplan.vehicleLayouts
        .flatMap((vehicleLayout) => vehicleLayout.pois)
        .filter((poi) => poi.areaId === area.id)
        .map((poi) => {
          const station = poi.stationIds
            .map((stationId) => stationsById.get(stationId))
            .find((candidate) => Boolean(candidate));

          if (!station) {
            const rectangleCenter = rotatePoint(
              poi.rectangle.centerX,
              poi.rectangle.centerY,
              poi.pose.x,
              poi.pose.y,
              deltaRad,
            );

            return {
              ...poi,
              rectangle: {
                ...poi.rectangle,
                centerX: rectangleCenter.x,
                centerY: rectangleCenter.y,
                angle: poi.rectangle.angle + deltaRad,
              },
              pose: {
                ...poi.pose,
                angle: poi.pose.angle + deltaRad,
              },
            };
          }

          const stationRectangle = station.cuboid.rectangle;
          const pose = rotatePoint(
            poi.pose.x,
            poi.pose.y,
            stationRectangle.centerX,
            stationRectangle.centerY,
            deltaRad,
          );
          const rectangleCenter = rotatePoint(
            poi.rectangle.centerX,
            poi.rectangle.centerY,
            stationRectangle.centerX,
            stationRectangle.centerY,
            deltaRad,
          );

          return {
            ...poi,
            rectangle: {
              ...poi.rectangle,
              centerX: rectangleCenter.x,
              centerY: rectangleCenter.y,
              angle: poi.rectangle.angle + deltaRad,
            },
            pose: {
              ...poi.pose,
              x: pose.x,
              y: pose.y,
              angle: poi.pose.angle + deltaRad,
            },
          };
        });

      const rotatedStations = areaStations.map((station) => ({
        ...station,
        cuboid: {
          ...station.cuboid,
          rectangle: {
            ...station.cuboid.rectangle,
            angle: station.cuboid.rectangle.angle + deltaRad,
          },
        },
      }));

      const zoom = canvas.getZoom();
      const duplicateStationIds = getDuplicateStationIds(rotatedStations);

      if (duplicateStationIds.length > 0) {
        canvas.removeElements(duplicateStationIds);
      }

      canvas.updateElements([
        ...poiMapper(rotatedPois, [], zoom, true, true),
        ...stationMapper(rotatedStations, true),
      ]);
    },
    [selectedAreas],
  );

  const onAngleCommit = useCallback(
    (angleRad: number) => {
      fddmSvc.setPreference(`${preferenceKey}.angle`, angleRad);
      updateConstellationSpecs((spec) => ({ ...spec, angle: angleRad }));
    },
    [fddmSvc, preferenceKey, updateConstellationSpecs],
  );

  const onStackingChange = useCallback(
    (stacking: ConstellationSpecType['stacking']) => {
      fddmSvc.setPreference(`${preferenceKey}.stacking`, stacking);

      updateConstellationSpecs((spec) => ({ ...spec, stacking }));
    },
    [fddmSvc, preferenceKey, updateConstellationSpecs],
  );

  const onMinMarginChange = useCallback(
    (newValueMm: number) => {
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference(`${preferenceKey}.spacing.minMargin`, newValue);
      updateConstellationSpecs((spec) => ({
        ...spec,
        spacing: { ...spec.spacing, minMargin: newValue },
      }));
    },
    [fddmSvc, preferenceKey, updateConstellationSpecs],
  );

  const onMinGapChange = useCallback(
    (newValueMm: number) => {
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference(`${preferenceKey}.spacing.minGap`, newValue);
      updateConstellationSpecs((spec) => ({
        ...spec,
        spacing: { ...spec.spacing, minGap: newValue },
      }));
    },
    [fddmSvc, preferenceKey, updateConstellationSpecs],
  );

  const onDistributionChange = useCallback(
    (newValue: ConstellationSpecType['distribution']) => {
      fddmSvc.setPreference(`${preferenceKey}.distribution`, newValue);
      updateConstellationSpecs((spec) => ({ ...spec, distribution: newValue }));
    },
    [fddmSvc, preferenceKey, updateConstellationSpecs],
  );

  if (selectedAreas.length === 0) return null;

  return (
    <>
      <Angle
        parentRotationRad={parentRotationRad ?? undefined}
        valueRad={angle}
        onCommit={onAngleCommit}
        onPreview={isSingleArea ? onAnglePreview : undefined}
      />

      <PropertiesLabel i18nkey='interface:properties.area.stacking'>
        <SelectInput
          options={stackingOptions}
          onChange={onStackingChange}
          value={stacking ?? undefined}
          showMixed
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.spacing'>
        <Spacing
          margin={minMargin === null ? null : NumUtils.metersToMmRounded(minMargin)}
          gap={minGap === null ? null : NumUtils.metersToMmRounded(minGap)}
          iconRotationDeg={spacingIconRotationDeg}
          onMarginChange={onMinMarginChange}
          onGapChange={onMinGapChange}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.distribution'>
        <SelectInput
          options={distributionOptions}
          onChange={onDistributionChange}
          value={distribution ?? undefined}
          showMixed
        />
      </PropertiesLabel>
    </>
  );
}

export const ConstellationSpec = memo(ConstellationSpecComponent);
